import type { Profile } from '@tunasong/schemas'
import type { FC } from 'react'
import { getColorCSS, getProfileColor } from '../color/index.js'
import { Avatar } from '../primitives/avatar.js'
import moduleStyles from './user-avatar.module.css'

/** How long a picture may take to load before the coloured initial shows in its place */
const fallbackDelayMs = 600

export interface ProfileAvatarImageProps {
  /** Its color, or a color hashed from its userId, fills the initial */
  profile: Partial<Profile>
  /** Diameter in px */
  px: number
  className?: string
  /** Show the picture when the profile has one */
  picture?: boolean
  alt?: string
  fallbackName?: string
}

/** A user's picture, or their initial on their profile color. Presentational: no data loading, no interaction. */
export const ProfileAvatarImage: FC<ProfileAvatarImageProps> = ({
  profile,
  px,
  className,
  picture = true,
  alt,
  fallbackName,
}) => {
  const { colorName } = getProfileColor(profile)
  const name = profile.name ?? profile.email ?? fallbackName
  const pictureUrl = picture && profile.picture ? profile.picture : undefined

  return (
    <Avatar
      className={className}
      src={pictureUrl}
      alt={alt ?? name}
      style={{ width: px, height: px, backgroundColor: 'transparent' }}
      // The fallback shows whenever the picture is missing, still loading or fails (e.g., a dead
      // IdP link), so it always carries the coloured initial. With a picture, wait before showing it
      // so a picture that loads normally never flashes the initial.
      fallbackProps={{
        className: moduleStyles.fallback,
        style: { backgroundColor: getColorCSS(colorName) },
        delay: pictureUrl ? fallbackDelayMs : undefined,
      }}
    >
      {name?.[0]?.toUpperCase()}
    </Avatar>
  )
}
